/**
 * RentWizard — Step 3 of the public Rent flow.
 *
 * 3-step accordion: Personal Information -> Payment Information -> Review.
 * Sticky right-rail with the live quote (Due Today + Future Month).
 *
 * Dynamic fields: when a unit_type is an outdoor parking / boat-storage
 * type, the Boat/Trailer/Vehicle/Motor sections render. For interior
 * climate or drive-up units they're hidden.
 */

import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  MapPin, ChevronRight, ChevronLeft, CheckCircle2, AlertTriangle,
  ShieldCheck, RefreshCw, ArrowLeft, Lock,
} from 'lucide-react';
import StripeTrustBadge from '../components/StripeTrustBadge';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8002';

const money = (v) => `$${Number(v || 0).toFixed(2)}`;

// 3-step flow (re-locked 2026-06-01 to match ESS literal screens):
//   1. Personal Information   (contact + personal + military + property)
//   2. Rental Details         (move-in date + protection plan + embedded
//                              lease + typed-name signature +
//                              "Sign Lease - Continue to Pay" button
//                              signs the lease and advances to step 3)
//   3. Pay                    (summary recap + "Pay $X" button -> Stripe
//                              Checkout). Card is collected by Stripe;
//                              mySMS360 never sees card data.
// Lease is shown + signed BEFORE payment. Mirrors a physical-office
// flow and gives operators a legal eviction basis when payment lands.
const TABS = [
  { key: 'personal', n: 1, label: 'Personal Information' },
  { key: 'rental',   n: 2, label: 'Rental Details' },
  { key: 'pay',      n: 3, label: 'Pay' },
];

// Unit-type categories that need the property-detail sections (LEGACY
// fallback, used only when the unit type has no storage_kind set).
const VEHICLE_CATEGORIES = new Set([
  'outdoor_parking', 'parking', 'rv_storage', 'boat_storage',
]);

// storage_kind values that SHOW the vehicle/boat/trailer property fields.
// Operator-picked at unit-type setup (ESS-style). Only NON_VEHICLE_ENCLOSED
// hides them; the other three show them.
const STORAGE_KIND_SHOW_VEHICLE = new Set([
  'enclosed_both', 'outdoor', 'outdoor_both',
]);

/**
 * Should the wizard present vehicle/boat/trailer property fields for this
 * unit type? Option A: if storage_kind is set, it's authoritative; otherwise
 * fall back to the legacy category check so existing/unmigrated unit types
 * behave exactly as before.
 */
function unitNeedsPropertyFields(unitType) {
  const kind = unitType?.storage_kind;
  if (kind) return STORAGE_KIND_SHOW_VEHICLE.has(kind);
  return VEHICLE_CATEGORIES.has(unitType?.category);
}

const ProgressDots = ({ current }) => (
  <div className="flex items-center justify-center gap-0 mb-8 max-w-md mx-auto">
    {TABS.map((t, idx) => {
      const isActive = t.key === current;
      const isDone = TABS.findIndex((x) => x.key === current) > idx;
      return (
        <React.Fragment key={t.key}>
          <div className="flex flex-col items-center">
            <div
              className={`h-9 w-9 rounded-full flex items-center justify-center text-[13px] font-semibold ${
                isActive
                  ? 'bg-blue-600 text-white'
                  : isDone
                  ? 'bg-emerald-600 text-white'
                  : 'bg-white border border-slate-300 text-slate-400'
              }`}
            >
              {isDone ? <CheckCircle2 className="h-4 w-4" /> : t.n}
            </div>
            <div className={`text-[11px] mt-1 text-center w-24 ${isActive ? 'text-blue-600 font-medium' : 'text-slate-500'}`}>
              {t.label}
            </div>
          </div>
          {idx < TABS.length - 1 && (
            <div className={`h-px flex-1 mt-[-20px] mx-1 ${isDone ? 'bg-emerald-300' : 'bg-slate-200'}`} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

const Section = ({ title, children, defaultOpen = true, required = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-slate-200 rounded-lg bg-white mb-4 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50"
      >
        <span className="text-[14px] font-semibold text-slate-900 flex items-center gap-2">
          {title}
          {required && (
            <span className="text-[10px] font-semibold text-rose-600 uppercase tracking-wider">Required</span>
          )}
        </span>
        <span className="text-[11px] text-slate-400">{open ? 'Hide' : 'Show'}</span>
      </button>
      {open && <div className="px-4 pb-4 pt-1 space-y-3">{children}</div>}
    </div>
  );
};

const Field = ({ label, required, children, hint }) => (
  <div>
    <label className="block text-[12px] font-medium text-slate-700 mb-1">
      {label} {required && <span className="text-rose-500">*</span>}
    </label>
    {children}
    {hint && <p className="text-[10px] text-slate-400 mt-0.5">{hint}</p>}
  </div>
);

const Input = (props) => (
  <input
    {...props}
    className={`w-full border border-slate-300 rounded px-3 py-1.5 text-[13px] focus:outline-none focus:border-blue-500 ${props.className || ''}`}
  />
);

const Select = (props) => (
  <select
    {...props}
    className={`w-full border border-slate-300 rounded px-3 py-1.5 text-[13px] focus:outline-none focus:border-blue-500 ${props.className || ''}`}
  />
);

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------

const RentWizard = ({ selection, onBack }) => {
  const facilityId = selection?.facility?.id;
  const unitType = selection?.unit_type;
  const unitId = selection?.unit_id;
  const unitNumber = selection?.unit_number;

  // Sync wizard step with URL hash so browser back/forward steps through
  // the wizard rather than navigating off to the marketing site.
  const VALID_TABS = ['personal', 'rental', 'pay', 'done'];
  const initialTab = (() => {
    if (typeof window === 'undefined') return 'personal';
    const h = window.location.hash.replace('#', '');
    return VALID_TABS.includes(h) ? h : 'personal';
  })();
  const [tab, _setTab] = useState(initialTab);
  // Track when a tab change is driven by popstate so dependent effects
  // (regenerateDraft) can skip work that should only fire on forward
  // navigation, not on back-button restores.
  const fromPopRef = useRef(false);
  const setTab = useCallback((next) => {
    _setTab(next);
    if (typeof window !== 'undefined' && next) {
      try {
        if (window.location.hash !== `#${next}`) {
          window.history.pushState({ wizardTab: next }, '', `#${next}`);
        }
      } catch (_) {}
    }
  }, []);

  // Seed a sentinel history entry for the wizard's first step on mount.
  // Without this, BACK from #rental lands on the bare /rent URL that
  // RentFlow owns -- but RentFlow.stage is still 'wizard' in React state,
  // so the wizard stays mounted with tab='personal' while the URL bar
  // shows no hash. The very next BACK then exits the SPA entirely, which
  // on a facility subdomain triggers SubdomainRedirect's window.location
  // .replace() mid-render and tears down the wizard while in-flight
  // axios calls (regenerateDraft / loadQuote / plans) resolve into an
  // unmounting tree -- producing the "Something went wrong" crash.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      if (!window.location.hash) {
        window.history.replaceState(
          { wizardTab: 'personal' },
          '',
          '#personal',
        );
      }
    } catch (_) {}
    // Mount-only; intentionally no deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Browser back/forward -> update tab state to match URL hash. When the
  // hash is gone entirely (user backed PAST the wizard's seed entry),
  // delegate to onBack so RentFlow.stage flips back to 'list' instead of
  // leaving the wizard mounted on a bare URL.
  useEffect(() => {
    const onPop = () => {
      const raw = window.location.hash.replace('#', '');
      if (VALID_TABS.includes(raw)) {
        fromPopRef.current = true;
        _setTab(raw);
      } else {
        // No recognized hash: user backed out of the wizard. Tell
        // RentFlow to return to its list view rather than silently
        // re-rendering as 'personal' on a URL we no longer own.
        if (typeof onBack === 'function') {
          onBack();
        } else {
          fromPopRef.current = true;
          _setTab('personal');
        }
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [plans, setPlans] = useState([]);
  const [protectionTier, setProtectionTier] = useState('basic');
  const [moveInDate, setMoveInDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // After Step 1 the backend creates the customer + draft rental and
  // mints a signing token for the generated lease. The wizard stores
  // these so Step 2 can render the lease + collect signature, and Step
  // 3 can call /finalize with the rental_id.
  const [draft, setDraft] = useState(null);
  // {rental_id, customer_id, lease_id, signing_token, signing_url, amount_due}

  // Step 2 signature state.
  const [leaseDetails, setLeaseDetails] = useState(null);
  const [leaseLoading, setLeaseLoading] = useState(false);
  const [agreementChecked, setAgreementChecked] = useState(false);
  const [signatureName, setSignatureName] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [signedConfirmation, setSignedConfirmation] = useState(null);

  // Form state
  // SMS consent moved into the signed lease's Notice Agreement paragraph,
  // so the public form doesn't need an SMS opt-in checkbox. Lease signature
  // IS the consent under TCPA.
  // contact.first_name + contact.last_name are the canonical split fields.
  // We still send `name` (the combined string) for backward compatibility
  // with existing automation rules / lease templates that interpolate
  // `[[CUSTOMER_NAME]]`. The backend draft endpoint also reads first/last
  // and persists them to the customer record so the PTI bridge can emit
  // "LastName,FirstName" without parsing.
  const [contact, setContact] = useState({
    first_name: '', last_name: '',
    name: '', email: '', phone: '', address: '', city: '', state: 'TX', zip_code: '',
  });
  const [personal, setPersonal] = useState({
    date_of_birth: '', photo_id_url: '', drivers_license_exp: '',
    additional_access_name: '', additional_access_phone: '',
  });
  const [military, setMilitary] = useState({
    in_military: 'No', branch: '', active_duty: '', reserves_or_guard: '',
  });
  const [propType, setPropType] = useState('');     // boat | trailer | vehicle | other
  const [propFields, setPropFields] = useState({}); // free-form per type

  // ESS shows the "Type of Property to be Stored" selector on EVERY rental,
  // so the section always renders. The per-type detail blocks (boat /
  // trailer / vehicle) still only appear once a relevant type is chosen.
  // `isVehicleUnit` drives whether vehicle/trailer is the EXPECTED default
  // (parking/boat units) but no longer gates the section's presence.
  // eslint-disable-next-line no-unused-vars
  const isVehicleUnit = useMemo(
    () => unitNeedsPropertyFields(unitType),
    [unitType]
  );

  // Load protection plans + initial quote. Aborted on unmount to avoid
  // setState-after-unmount during cross-document navigation (the back-
  // button-into-marketing-site case).
  useEffect(() => {
    if (!facilityId) return;
    const controller = new AbortController();
    (async () => {
      try {
        const r = await axios.get(
          `${API_URL}/api/rent/protection-plans/${facilityId}`,
          { signal: controller.signal },
        );
        setPlans(r.data?.plans || []);
      } catch {}
    })();
    return () => controller.abort();
  }, [facilityId]);

  const quoteAbortRef = useRef(null);
  const loadQuote = useCallback(async () => {
    if (!facilityId || !unitType?.id) return;
    // Cancel any in-flight quote request before starting a new one.
    if (quoteAbortRef.current) quoteAbortRef.current.abort();
    const controller = new AbortController();
    quoteAbortRef.current = controller;
    setQuoteLoading(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/rent/${facilityId}/quote`,
        {
          unit_type_id: unitType.id,
          unit_id: unitId,
          move_in_date: moveInDate,
          protection_tier: protectionTier,
        },
        { signal: controller.signal },
      );
      if (!controller.signal.aborted) setQuote(r.data);
    } catch (e) {
      if (controller.signal.aborted) return;
      setError(e?.response?.data?.detail || e.message);
    } finally {
      if (!controller.signal.aborted) setQuoteLoading(false);
    }
  }, [facilityId, unitType?.id, unitId, moveInDate, protectionTier]);

  useEffect(() => {
    loadQuote();
    return () => {
      if (quoteAbortRef.current) quoteAbortRef.current.abort();
    };
  }, [loadQuote]);

  // Step 1 → Step 2: client-side navigation only. No backend call yet.
  const advanceToRental = () => {
    const missing = [];
    if (!contact.first_name) missing.push('First Name');
    if (!contact.last_name) missing.push('Last Name');
    if (!contact.email) missing.push('Email');
    if (!contact.phone) missing.push('Phone');
    if (!contact.address) missing.push('Address');
    if (!contact.city) missing.push('City');
    if (!contact.state) missing.push('State');
    if (!contact.zip_code) missing.push('Zip');

    // ESS-parity required fields (live on the collapsible "Required Details"
    // and "Property to be Stored" twisties, but still gate signup). DOB +
    // military disclosure are starred on the ESS intake; military is a
    // required SCRA yes/no. The military Select defaults to 'No', so it is
    // never empty — but we keep the missing-check for the explicit-choice
    // contract in case the default changes.
    if (!personal.date_of_birth) missing.push('Date of Birth');
    if (!military.in_military) missing.push('Military status (Yes/No)');

    // Property-to-be-stored only applies to vehicle-capable unit types
    // (storage_kind / category). Non-vehicle enclosed units skip it entirely
    // — nothing to ask. When it does apply, Type is required; once a
    // vehicle/trailer type is chosen, the ESS-starred VIN / License Plate /
    // Est. Value fields are required-with-"NA"-allowed.
    if (unitNeedsPropertyFields(unitType)) {
      if (!propType) {
        missing.push('Type of Property to be Stored');
      } else if (propType === 'vehicle') {
        if (!propFields.vin) missing.push('Vehicle VIN (type NA if none)');
        if (!propFields.plate) missing.push('Vehicle License Plate (type NA if none)');
        if (!propFields.est_value) missing.push('Vehicle Est. Value ($0 if not a vehicle)');
      } else if (propType === 'trailer') {
        if (!propFields.plate) missing.push('Trailer License Plate (type NA if none)');
        if (!propFields.vin) missing.push('Trailer VIN/Serial # (type NA if none)');
      }
    }

    if (missing.length) {
      setError(`Please fill in: ${missing.join(', ')}.`);
      return;
    }
    // Synthesize combined name so downstream consumers (lease template,
    // signature match check, automation rules) keep working unchanged.
    if (!contact.name) {
      setContact((c) => ({
        ...c,
        name: `${c.first_name.trim()} ${c.last_name.trim()}`,
      }));
    }
    setError(null);
    setTab('rental');
  };

  // Generate or regenerate the draft + lease for Step 2. Called on Step 2
  // entry and whenever TPP / move-in change. Each call creates a NEW
  // draft rental in the DB; the prior one is abandoned (status="draft"
  // is filtered out of the operator dashboard, and the Case-A nudge
  // sweep cleans it up if it ever needs attention).
  const draftAbortRef = useRef(null);
  const regenerateDraft = useCallback(async () => {
    if (!facilityId || !unitId) return;
    if (draftAbortRef.current) draftAbortRef.current.abort();
    const controller = new AbortController();
    draftAbortRef.current = controller;
    setLeaseLoading(true);
    setError(null);
    try {
      const payload = {
        unit_type_id: unitType.id,
        unit_id: unitId,
        move_in_date: moveInDate,
        protection_tier: protectionTier,
        contact,
        personal,
        military,
        stored_property: propType ? { type: propType, fields: propFields } : null,
      };
      const r = await axios.post(
        `${API_URL}/api/rent/${facilityId}/draft`,
        payload,
        { signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      setDraft(r.data);
      // Pre-fill typed name. Tenant can edit; backend validates that the
      // typed name matches contact.name (case-insensitive, trimmed).
      setSignatureName((prev) => prev || contact.name);
      // Load lease details for the iframe.
      if (r.data?.signing_token) {
        try {
          const dr = await axios.get(
            `${API_URL}/api/public/leases/sign/${r.data.signing_token}`,
            { signal: controller.signal },
          );
          if (!controller.signal.aborted) setLeaseDetails(dr.data);
        } catch (dErr) {
          // Non-fatal — iframe will show fallback message
          if (!controller.signal.aborted) setLeaseDetails(null);
        }
      } else {
        setLeaseDetails(null);
      }
    } catch (e) {
      if (controller.signal.aborted) return;
      setError(e?.response?.data?.detail || e.message);
    } finally {
      if (!controller.signal.aborted) setLeaseLoading(false);
    }
  }, [facilityId, unitId, unitType?.id, moveInDate, protectionTier, contact, personal, military, propType, propFields]);

  // Abort any in-flight draft request on unmount so we don't setState into
  // a torn-down tree during cross-document navigation.
  useEffect(() => {
    return () => {
      if (draftAbortRef.current) draftAbortRef.current.abort();
    };
  }, []);

  // Auto-fire /draft when the tenant enters Step 2 (Rental Details).
  // We only call once per entry — subsequent move-in/TPP edits regenerate
  // via the edit-side-effect below. Skip the auto-fire when the tab
  // change came from popstate (back-button restore) so BACK from Step 3
  // into Step 2 does not mint a duplicate draft rental every time.
  useEffect(() => {
    if (fromPopRef.current) {
      fromPopRef.current = false;
      return;
    }
    if (tab === 'rental' && !draft?.signing_token && !leaseLoading) {
      regenerateDraft();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Regenerate the lease if the tenant changes move-in date or
  // protection tier WHILE on Step 2. Skip the very first run (initial
  // mount) — that's handled by the entry effect above.
  const initialLeaseRef = useRef(true);
  useEffect(() => {
    if (initialLeaseRef.current) {
      initialLeaseRef.current = false;
      return;
    }
    if (tab !== 'rental') return;
    // Only regen if we already have a draft (else entry-effect will fire).
    if (!draft?.signing_token) return;
    regenerateDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moveInDate, protectionTier]);

  // Step 2 → Step 3: sign the lease, then advance to Pay.
  const signAndContinue = async () => {
    if (!draft?.signing_token) {
      setError('The lease isn\'t ready yet. Please wait a moment and retry.');
      return;
    }
    if (!agreementChecked) {
      setError('Please check the agreement box to continue.');
      return;
    }
    if (!signatureName.trim()) {
      setError('Please type your name to sign.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const sigResp = await axios.post(
        `${API_URL}/api/public/leases/sign/${draft.signing_token}/submit`,
        {
          signature_name: signatureName.trim(),
          intent_acknowledged: true,
          consent_acknowledged: true,
        },
      );
      setSignedConfirmation(sigResp.data);
      setTab('pay');
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Step 3: Pay — call /finalize and redirect to Stripe Checkout.
  const finalizeAndPay = async () => {
    if (!draft?.rental_id) {
      setError('We lost track of your rental. Please go back and re-sign.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const finResp = await axios.post(
        `${API_URL}/api/rent/${facilityId}/finalize`,
        { rental_id: draft.rental_id },
      );
      if (finResp.data?.checkout_url) {
        window.location.href = finResp.data.checkout_url;
        return;
      }
      // No Stripe connected — show success state; operator collects manually.
      setTab('done');
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const goNext = () => {
    if (tab === 'personal') advanceToRental();
    else if (tab === 'rental') signAndContinue();
    else if (tab === 'pay') finalizeAndPay();
  };
  const goBack = () => {
    if (tab === 'pay') setTab('rental');
    else if (tab === 'rental') setTab('personal');
    else onBack?.();
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={onBack}
            className="inline-flex items-center gap-1 text-[12px] text-slate-500 hover:text-slate-700"
          >
            <ArrowLeft className="h-3 w-3" /> Back to listings
          </button>
          <div className="text-right">
            <h1 className="text-[15px] font-semibold text-slate-900">{selection?.facility?.name}</h1>
            <p className="text-[11px] text-slate-500">
              {selection?.facility?.address}, {selection?.facility?.city}
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        <ProgressDots current={tab} />

        {error && (
          <div className="rounded-xl border border-rose-200/80 bg-rose-50/50 p-3 mb-4 max-w-3xl mx-auto">
            <p className="text-[12px] text-rose-700 flex items-center gap-2"><AlertTriangle className="h-3 w-3" />{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2">
            {tab === 'personal' && (
              <PersonalForm
                contact={contact} setContact={setContact}
                personal={personal} setPersonal={setPersonal}
                military={military} setMilitary={setMilitary}
                propType={propType} setPropType={setPropType}
                propFields={propFields} setPropFields={setPropFields}
                showProperty={unitNeedsPropertyFields(unitType)}
              />
            )}
            {tab === 'rental' && (
              <RentalStep
                plans={plans}
                tier={protectionTier} setTier={setProtectionTier}
                moveInDate={moveInDate} setMoveInDate={setMoveInDate}
                isVehicle={unitNeedsPropertyFields(unitType)}
                lease={leaseDetails}
                leaseLoading={leaseLoading}
                signatureName={signatureName}
                setSignatureName={setSignatureName}
                agreementChecked={agreementChecked}
                setAgreementChecked={setAgreementChecked}
              />
            )}
            {tab === 'pay' && (
              <PayStep
                selection={selection}
                contact={contact}
                quote={quote}
                onEdit={(t) => setTab(t)}
              />
            )}
            {tab === 'done' && (
              <DoneStep reservation={draft} />
            )}

            {tab !== 'done' && (
              <div className="flex justify-between mt-5">
                <button
                  onClick={goBack}
                  className="inline-flex items-center gap-1 px-4 py-2 border border-slate-300 hover:bg-slate-50 rounded text-[13px] text-slate-700"
                >
                  <ChevronLeft className="h-3 w-3" /> Back
                </button>
                {tab === 'rental' ? (
                  <button
                    onClick={goNext}
                    disabled={submitting || leaseLoading || !agreementChecked || !signatureName.trim() || !draft?.signing_token}
                    className="inline-flex items-center gap-1 px-5 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded text-[13px] font-semibold"
                  >
                    {submitting ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Lock className="h-3 w-3" />}
                    Sign Lease &mdash; Continue to Pay
                  </button>
                ) : tab === 'pay' ? (
                  <button
                    onClick={goNext}
                    disabled={submitting}
                    className="inline-flex items-center gap-1 px-5 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded text-[13px] font-semibold"
                  >
                    {submitting ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Lock className="h-3 w-3" />}
                    Pay {quote?.due_today?.total ? money(quote.due_today.total) : ''}
                  </button>
                ) : (
                  <button
                    onClick={goNext}
                    disabled={submitting}
                    className="inline-flex items-center gap-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded text-[13px] font-medium"
                  >
                    {submitting ? <RefreshCw className="h-3 w-3 animate-spin" /> : null}
                    Next <ChevronRight className="h-3 w-3" />
                  </button>
                )}
              </div>
            )}
          </div>

          <aside className="lg:col-span-1">
            <RightRail
              facility={selection?.facility}
              unitType={unitType}
              unitNumber={unitNumber}
              moveInDate={moveInDate}
              quote={quote}
              loading={quoteLoading}
              onChangeUnit={onBack}
            />
          </aside>
        </div>
      </main>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Step 1 (Personal Information)
// ---------------------------------------------------------------------------

const PersonalForm = ({
  contact, setContact, personal, setPersonal, military, setMilitary,
  propType, setPropType, propFields, setPropFields, showProperty,
}) => {
  const setProp = (key, val) => setPropFields((p) => ({ ...p, [key]: val }));
  return (
    <>
      <Section title="Required Information">
        {/* Split first/last instead of a single Name field. The PTI gate
            integration emits "LastName,FirstName" verbatim from these —
            no parsing — so multi-word last names ("Van Houten",
            "St. John") and hyphenated names ("Smith-Johnson") just work. */}
        <div className="grid grid-cols-2 gap-3">
          <Field label="First Name" required>
            <Input
              value={contact.first_name}
              onChange={(e) => {
                const first_name = e.target.value;
                setContact((c) => ({
                  ...c,
                  first_name,
                  name: `${first_name} ${c.last_name}`.trim(),
                }));
              }}
            />
          </Field>
          <Field label="Last Name" required>
            <Input
              value={contact.last_name}
              onChange={(e) => {
                const last_name = e.target.value;
                setContact((c) => ({
                  ...c,
                  last_name,
                  name: `${c.first_name} ${last_name}`.trim(),
                }));
              }}
            />
          </Field>
        </div>
        <Field label="Address" required>
          <Input value={contact.address} onChange={(e) => setContact({ ...contact, address: e.target.value })} />
        </Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="City" required><Input value={contact.city} onChange={(e) => setContact({ ...contact, city: e.target.value })} /></Field>
          <Field label="State" required><Input value={contact.state} onChange={(e) => setContact({ ...contact, state: e.target.value })} /></Field>
          <Field label="Zip Code" required><Input value={contact.zip_code} onChange={(e) => setContact({ ...contact, zip_code: e.target.value })} /></Field>
        </div>
        <Field label="Email" required>
          <Input type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
        </Field>
        <Field label="Cell Phone" required>
          <Input value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} />
        </Field>
      </Section>

      {/* ESS-parity required fields, kept collapsed-by-default like the
          old "optional" twisty so the form stays short, but these DO gate
          signup. DOB + military disclosure (SCRA) are starred on the ESS
          intake; military is a required yes/no. Validation in
          advanceToRental enforces them. The truly-optional fields (driver's
          license exp, additional access person) stay non-required below. */}
      <Section title="Required Details" defaultOpen={false} required>
        <p className="text-[11px] text-slate-500 -mt-1 mb-2">
          A few more details we need to complete your rental.
        </p>
        <Field label="Date of Birth" required>
          <Input type="date" value={personal.date_of_birth} onChange={(e) => setPersonal({ ...personal, date_of_birth: e.target.value })} />
        </Field>

        <div className="border-t border-slate-100 pt-3 mt-2">
          <Field label="Are you currently in the U.S. military?" required hint="Required by the Servicemembers Civil Relief Act (SCRA).">
            <Select value={military.in_military} onChange={(e) => setMilitary({ ...military, in_military: e.target.value })}>
              <option>No</option>
              <option>Yes</option>
            </Select>
          </Field>
          {military.in_military === 'Yes' && (
            <>
              <Field label="Branch"><Input value={military.branch} onChange={(e) => setMilitary({ ...military, branch: e.target.value })} /></Field>
              <Field label="Active Duty (Y/N)"><Input value={military.active_duty} onChange={(e) => setMilitary({ ...military, active_duty: e.target.value })} /></Field>
              <Field label="Reserves / Guard (Y/N)"><Input value={military.reserves_or_guard} onChange={(e) => setMilitary({ ...military, reserves_or_guard: e.target.value })} /></Field>
            </>
          )}
        </div>
      </Section>

      {/* Property to be Stored — shown ONLY for vehicle-capable unit types
          (storage_kind / category). Non-vehicle enclosed units skip it
          entirely (household/business goods — nothing to collect). When
          shown, Type is required; per-type detail blocks appear once a type
          is chosen, with VIN/plate/value required-with-"NA". */}
      {showProperty && (
        <Section title="Property to be Stored" defaultOpen={false} required>
          <Field label="Type of Property" required>
            <Select value={propType} onChange={(e) => setPropType(e.target.value)}>
              <option value="">Select...</option>
              <option value="other">Other (household, business)</option>
              <option value="vehicle">Vehicle (auto, motorcycle, RV)</option>
              <option value="boat">Boat</option>
              <option value="trailer">Trailer</option>
            </Select>
          </Field>

          {propType === 'boat' && <BoatFields fields={propFields} set={setProp} />}
          {propType === 'trailer' && <TrailerFields fields={propFields} set={setProp} />}
          {propType === 'vehicle' && <VehicleFields fields={propFields} set={setProp} />}
        </Section>
      )}

      {/* Genuinely optional — operator can collect later for lien/eviction. */}
      <Section title="Additional Details (optional)" defaultOpen={false}>
        <Field label="Driver's License Expiration">
          <Input type="date" value={personal.drivers_license_exp} onChange={(e) => setPersonal({ ...personal, drivers_license_exp: e.target.value })} />
        </Field>
        <Field label="Additional Access Person (name)" hint="Lessor may share space number, access code, or assist with lock removal for this person.">
          <Input value={personal.additional_access_name} onChange={(e) => setPersonal({ ...personal, additional_access_name: e.target.value })} />
        </Field>
        <Field label="Additional Access Person (phone)">
          <Input value={personal.additional_access_phone} onChange={(e) => setPersonal({ ...personal, additional_access_phone: e.target.value })} />
        </Field>
      </Section>
    </>
  );
};

// Per-property-type fields. Kept small and obvious.
const BoatFields = ({ fields, set }) => (
  <>
    <div className="grid grid-cols-2 gap-3">
      <Field label="TX #"><Input value={fields.tx_no || ''} onChange={(e) => set('tx_no', e.target.value)} /></Field>
      <Field label="HIN / Serial #"><Input value={fields.hin || ''} onChange={(e) => set('hin', e.target.value)} /></Field>
    </div>
    <Field label="Make, Model & Year Built"><Input value={fields.make_model || ''} onChange={(e) => set('make_model', e.target.value)} /></Field>
    <div className="grid grid-cols-3 gap-3">
      <Field label="Length"><Input value={fields.length || ''} onChange={(e) => set('length', e.target.value)} /></Field>
      <Field label="Est. Value $"><Input value={fields.est_value || ''} onChange={(e) => set('est_value', e.target.value)} /></Field>
      <Field label="Home Port"><Input value={fields.home_port || ''} onChange={(e) => set('home_port', e.target.value)} /></Field>
    </div>
    <Field label="Boat Name"><Input value={fields.boat_name || ''} onChange={(e) => set('boat_name', e.target.value)} /></Field>
    <Field label="Is Tenant the registered owner?">
      <Select value={fields.is_owner || ''} onChange={(e) => set('is_owner', e.target.value)}>
        <option value="">Select</option><option>Yes</option><option>No</option>
      </Select>
    </Field>
    {fields.is_owner === 'No' && (
      <Field label="Registered owner name & address"><Input value={fields.owner_info || ''} onChange={(e) => set('owner_info', e.target.value)} /></Field>
    )}
    <Field label="Motor - Make / HP / In or Outboard"><Input value={fields.motor || ''} onChange={(e) => set('motor', e.target.value)} /></Field>
  </>
);

const TrailerFields = ({ fields, set }) => (
  <>
    <div className="grid grid-cols-2 gap-3">
      <Field label="License Plate (NA if none)" required><Input value={fields.plate || ''} onChange={(e) => set('plate', e.target.value)} /></Field>
      <Field label="State Registered"><Input value={fields.state_reg || ''} onChange={(e) => set('state_reg', e.target.value)} /></Field>
    </div>
    <Field label="VIN / Serial # (NA if none)" required><Input value={fields.vin || ''} onChange={(e) => set('vin', e.target.value)} /></Field>
    <div className="grid grid-cols-2 gap-3">
      <Field label="Expiration"><Input value={fields.expiration || ''} onChange={(e) => set('expiration', e.target.value)} /></Field>
      <Field label="Est. Value $"><Input value={fields.est_value || ''} onChange={(e) => set('est_value', e.target.value)} /></Field>
    </div>
    <Field label="Insurance Provider"><Input value={fields.insurance_provider || ''} onChange={(e) => set('insurance_provider', e.target.value)} /></Field>
    <Field label="Policy #"><Input value={fields.policy_no || ''} onChange={(e) => set('policy_no', e.target.value)} /></Field>
  </>
);

const VehicleFields = ({ fields, set }) => (
  <>
    <Field label="Type (Automobile / RV / Motorcycle / etc.)"><Input value={fields.type || ''} onChange={(e) => set('type', e.target.value)} /></Field>
    <Field label="Make, Model & Year"><Input value={fields.make_model || ''} onChange={(e) => set('make_model', e.target.value)} /></Field>
    <div className="grid grid-cols-2 gap-3">
      <Field label="VIN (NA if none)" required><Input value={fields.vin || ''} onChange={(e) => set('vin', e.target.value)} /></Field>
      <Field label="License Plate (NA if none)" required><Input value={fields.plate || ''} onChange={(e) => set('plate', e.target.value)} /></Field>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <Field label="State Registered"><Input value={fields.state_reg || ''} onChange={(e) => set('state_reg', e.target.value)} /></Field>
      <Field label="Est. Value $"><Input value={fields.est_value || ''} onChange={(e) => set('est_value', e.target.value)} /></Field>
    </div>
    <Field label="Insurance Provider"><Input value={fields.insurance_provider || ''} onChange={(e) => set('insurance_provider', e.target.value)} /></Field>
    <Field label="Policy #"><Input value={fields.policy_no || ''} onChange={(e) => set('policy_no', e.target.value)} /></Field>
  </>
);

// ---------------------------------------------------------------------------
// Step 2 (Rental Details) — move-in date + protection plan + embedded
// lease PDF + typed-name signature. Single "Sign Lease - Continue to Pay"
// button at the bottom signs the lease and advances to Step 3.
// ---------------------------------------------------------------------------

const RentalStep = ({
  plans, tier, setTier, moveInDate, setMoveInDate, isVehicle,
  lease, leaseLoading,
  signatureName, setSignatureName,
  agreementChecked, setAgreementChecked,
}) => {
  // Vehicle / boat / trailer / outdoor parking units: TPP doesn't apply.
  // Per the lease's TPP Addendum §4 Excluded Property, vehicles and items
  // stored outside a locked unit are explicitly NOT covered. Force tier to
  // 'none' so the backend quote skips the protection-plan line item, and
  // hide the section entirely.
  useEffect(() => {
    if (isVehicle && tier !== 'none') setTier('none');
  }, [isVehicle, tier, setTier]);

  return (
    <>
      <Section title="Move-in Date">
        <Field label="When do you want to start?" required>
          <Input type="date" value={moveInDate} onChange={(e) => setMoveInDate(e.target.value)} />
        </Field>
      </Section>

      {!isVehicle && (
        <Section title="Protection Plan">
          <p className="text-[12px] text-slate-500 mb-2 flex items-center gap-1">
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
            Required. Covers your stored items against theft, fire, vermin, water damage, and more.
          </p>
          {plans.length === 0 ? (
            <p className="text-[12px] text-slate-400">Loading plans...</p>
          ) : (
            <div className="space-y-2">
              {plans.map((p) => (
                <label
                  key={p.tier}
                  className={`flex items-center justify-between p-3 rounded border cursor-pointer ${
                    tier === p.tier ? 'border-blue-500 bg-blue-50/40' : 'border-slate-200'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <input type="radio" checked={tier === p.tier} onChange={() => setTier(p.tier)} />
                    <div>
                      <div className="text-[13px] font-medium text-slate-900">{p.label}</div>
                      <div className="text-[11px] text-slate-500">Up to {money(p.coverage_limit)} coverage</div>
                    </div>
                  </div>
                  <div className="text-[13px] font-semibold text-slate-900">{money(p.monthly_premium)}/mo</div>
                </label>
              ))}
            </div>
          )}
        </Section>
      )}

      {isVehicle && (
        <Section title="Protection Plan">
          <p className="text-[12px] text-slate-500">
            The tenant protection plan is not available for vehicle, boat, trailer, or outdoor-parking units.
            Tenant carries their own auto / boat / RV insurance per the lease's Vehicle &amp; Boat Addendum.
          </p>
        </Section>
      )}

      <Section title="Storage Rental Agreement">
        <p className="text-[12px] text-slate-500 mb-3">
          Please review the entire agreement below before signing. You can scroll within the document.
        </p>
        {leaseLoading ? (
          <div className="h-[420px] flex items-center justify-center bg-slate-50 rounded border border-slate-200">
            <RefreshCw className="h-5 w-5 text-slate-400 animate-spin" />
            <span className="ml-2 text-[12px] text-slate-500">Loading lease...</span>
          </div>
        ) : lease?.pdf_url ? (
          <iframe
            src={`${API_URL}${lease.pdf_url}`}
            title="Lease agreement"
            className="w-full h-[480px] border border-slate-200 rounded"
          />
        ) : (
          <div className="h-[420px] flex items-center justify-center bg-rose-50/50 rounded border border-rose-200">
            <span className="text-[12px] text-rose-700 text-center px-4">
              Lease isn't ready. Please call the facility &mdash; they can email you a signing link.
            </span>
          </div>
        )}
      </Section>

      <Section title="Sign">
        <label className="flex items-start gap-2 text-[12px] text-slate-700 mb-3">
          <input
            type="checkbox"
            checked={agreementChecked}
            onChange={(e) => setAgreementChecked(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            I have read and agree to the storage rental agreement above. I understand
            that this is a legally binding contract and that my unit access is
            conditional on this signed agreement.
          </span>
        </label>
        <Field
          label="Type your full name to sign"
          required
          hint="Your typed name + IP + timestamp constitutes a legally binding electronic signature (E-SIGN Act). Must match the name you entered on Step 1."
        >
          <Input
            value={signatureName}
            onChange={(e) => setSignatureName(e.target.value)}
            placeholder="Full legal name"
          />
        </Field>
        <p className="text-[11px] text-slate-400 mt-3">
          Clicking <strong>Sign Lease &mdash; Continue to Pay</strong> below
          signs your lease. You will then be taken to the Pay step.
        </p>
      </Section>

      <p className="text-[11px] text-slate-400 px-2 mt-3">
        Card or ACH payment is collected on Stripe's hosted checkout after you sign.
      </p>
    </>
  );
};

// ---------------------------------------------------------------------------
// Step 3 (Pay) — read-only summary of what the tenant signed + a single
// "Pay $X" button that opens Stripe Checkout.
// ---------------------------------------------------------------------------

const PayStep = ({ selection, contact, quote, onEdit }) => (
  <>
    <Section title="Almost done — confirm and pay">
      <p className="text-[12px] text-slate-600 mb-3">
        Your lease has been signed. Click <strong>Pay</strong> below to
        complete your move-in payment via Stripe's hosted checkout.
      </p>
      <div className="grid grid-cols-2 gap-4 text-[12px]">
        <div>
          <div className="text-slate-400 uppercase tracking-wider text-[10px] mb-1">Unit</div>
          <div className="text-slate-900 font-medium">{selection?.unit_type?.name}</div>
          <div className="text-slate-700">{selection?.unit_number ? `Unit ${selection.unit_number}` : ''}</div>
        </div>
        <div>
          <div className="text-slate-400 uppercase tracking-wider text-[10px] mb-1">Tenant</div>
          <div className="text-slate-900 font-medium">{contact.name || '-'}</div>
          <div className="text-slate-700">{contact.email}</div>
          <div className="text-slate-700">{contact.phone}</div>
          <button onClick={() => onEdit('personal')} className="text-blue-600 text-[11px] mt-1">Edit</button>
        </div>
        <div>
          <div className="text-slate-400 uppercase tracking-wider text-[10px] mb-1">Plan</div>
          <div className="text-slate-900 font-medium">{quote?.protection_plan?.label || '-'}</div>
        </div>
        <div>
          <div className="text-slate-400 uppercase tracking-wider text-[10px] mb-1">Move-in</div>
          <div className="text-slate-900 font-medium">{quote?.move_in_date || '-'}</div>
        </div>
      </div>
    </Section>
    <StripeTrustBadge size="standard" />
  </>
);

// ---------------------------------------------------------------------------
// Done step
// ---------------------------------------------------------------------------

const DoneStep = ({ reservation }) => (
  <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-6 text-center">
    <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto mb-3" />
    <h2 className="text-lg font-semibold text-slate-900 mb-1">Reservation received</h2>
    <p className="text-[13px] text-slate-600 max-w-md mx-auto">
      Your unit is reserved. The facility doesn't have online payment set up yet — someone will be in touch shortly to collect the first month and walk you through the gate code.
    </p>
    {reservation?.unit_number && (
      <p className="text-[12px] text-slate-500 mt-3">
        Confirmation: Unit {reservation.unit_number} - {money(reservation.amount_due)}
      </p>
    )}
  </div>
);

// ---------------------------------------------------------------------------
// Right rail (live quote)
// ---------------------------------------------------------------------------

const RightRail = ({ facility, unitType, unitNumber, moveInDate, quote, loading, onChangeUnit }) => (
  <div className="lg:sticky lg:top-6">
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <div className="p-4 border-b border-slate-100">
        {unitType?.photo_url && (
          <img src={unitType.photo_url} alt={unitType.name} className="w-full h-32 object-cover rounded-lg mb-3" />
        )}
        <div className="text-[13px] font-semibold text-slate-900">
          {unitNumber ? `${unitNumber} - ` : ''}{unitType?.name}
        </div>
        <div className="text-[14px] font-bold text-slate-900 mt-1">
          {money(unitType?.monthly_rate)}/Month
        </div>
        <div className="text-[11px] text-slate-500 mt-1">
          {facility?.name}<br/>
          {facility?.address}<br/>
          {facility?.city}, {facility?.state} {facility?.zip_code}<br/>
          {facility?.phone}
        </div>
        <button onClick={onChangeUnit} className="text-[11px] text-blue-600 mt-2 hover:underline">Change Unit</button>
      </div>

      {loading ? (
        <div className="p-4 text-[12px] text-slate-400">Calculating...</div>
      ) : quote ? (
        <>
          <div className="p-4 border-b border-slate-100">
            <h3 className="text-[12px] font-semibold text-slate-700 uppercase tracking-wider mb-2">Due Today</h3>
            <div className="space-y-1.5">
              {(quote.due_today?.lines || []).map((line, idx) => (
                line.amount > 0 ? (
                  <div key={idx} className="flex justify-between text-[12px]">
                    <div>
                      <div className="text-slate-700">{line.label}</div>
                      {line.period && <div className="text-[10px] text-slate-400">{line.period}</div>}
                    </div>
                    <div className="text-slate-900 font-medium">{money(line.amount)}</div>
                  </div>
                ) : null
              ))}
            </div>
            <div className="flex justify-between font-bold text-[14px] text-slate-900 pt-2 mt-2 border-t border-slate-200">
              <span>Total Today</span>
              <span>{money(quote.due_today?.total)}</span>
            </div>
          </div>

          <div className="p-4">
            <h3 className="text-[12px] font-semibold text-slate-700 uppercase tracking-wider mb-2">Future Month Charges</h3>
            <div className="space-y-1.5">
              {(quote.future_month?.lines || []).map((line, idx) => (
                line.amount > 0 ? (
                  <div key={idx} className="flex justify-between text-[12px]">
                    <span className="text-slate-700">{line.label}</span>
                    <span className="text-slate-900 font-medium">{money(line.amount)}</span>
                  </div>
                ) : null
              ))}
            </div>
            <div className="flex justify-between font-semibold text-[13px] text-slate-900 pt-2 mt-2 border-t border-slate-200">
              <span>Total</span>
              <span>{money(quote.future_month?.total)}</span>
            </div>
          </div>
        </>
      ) : null}
    </div>
  </div>
);

export default RentWizard;
